import { Formik } from "formik";
import React, { useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { Button,  ThemeProviderV2 } from '@blueprint/ui';
import { useModal } from "react-modal-hook";
import * as yup from "yup";

import usersApi, { useUserById } from "../../../api/users";
import { UserStatus } from "../../../types/users";
import { Loader, toast } from "../../../common/components/core";
import { TextareaField } from "../../../common/components/form";
import { useAuthErrorHandlerCreator } from "../../../common/hooks";
import { useGeneralErrorHandlerCreator } from "../../../common/hooks/useGeneralErrorHandler";
import { GenericError, composeErrorHandlers } from "../../../utils/errorHandling";
import { FormSubmissionHandler } from "../../../types/common";


export type SARejectFormValues = {
    comment: string;
};

const schema: yup.SchemaOf<SARejectFormValues> = yup.object().shape({
    comment: yup.string().required(),
});

type ReviewRejectHandler = {
    isSubmitting: boolean;
    handler: (
        name: string,
        status: UserStatus
    ) => void;
};

const useReviewRejectHandler = (userId: string): ReviewRejectHandler => {
    const [ isSubmitting, setSubmitting ] = useState(false);
    const [ name, setName ] = useState<string>("");
    const [ status, setStatus ] = useState<UserStatus>();
    const onAuthError = useAuthErrorHandlerCreator();
    const useGeneralError = useGeneralErrorHandlerCreator();
    const { mutate } = useUserById(userId);
    const typeText = status === UserStatus.PENDING_CREATE_APPROVAL ? "Creation" : "Edit";
    const typeTextLow = status === UserStatus.PENDING_CREATE_APPROVAL ? "creation" : "edit";

    const [show, hide] = useModal(() => {
        const confirm: FormSubmissionHandler<SARejectFormValues> = async (values) => {
            setSubmitting(true);
            try {
                await usersApi.review(userId, 1, values.comment);
                mutate();
                toast(`user ${name} ${typeText} rejected`);
            } catch (e) {
                composeErrorHandlers(
                    onAuthError,
                    useGeneralError,
                )(e as GenericError);
            }
            hide();
            setSubmitting(false);
        };

        return (
            <ThemeProviderV2>
            <Modal show onHide={hide} backdrop="static" centered>
                <Formik
                    validationSchema={schema}
                    validateOnBlur={false}
                    validateOnChange={false}
                    onSubmit={confirm}
                    initialValues={{
                        comment: "",
                    }}
                >
                    {({ handleSubmit }) => (
                        <Loader overlay isLoading={isSubmitting}>
                            <Form
                                autoComplete="off"
                                noValidate
                                onSubmit={handleSubmit}
                            >
                                <Modal.Header>
                                    <h2 className="mb-0">
                                        {`Reject User ${typeText}`}
                                    </h2>
                                </Modal.Header>
                                <Modal.Body>
                                    <p>{`Are you sure you want to reject this user ${typeTextLow}?`}</p>
                                    <TextareaField
                                        name="comment"
                                        label="Comment"
                                        maxLength={256}
                                        required={true}
                                        isAllowed={(formattedValue) => formattedValue.length < 256}
                                    />
                                </Modal.Body>

                                <Modal.Footer className="d-flex justify-content-between px-3">
                                    <Button variant="light.tertiary" onClick={hide}>
                                        Cancel
                                    </Button>
                                    <Button variant="light.primary" type="submit">
                                        Confirm
                                    </Button>
                                </Modal.Footer>
                            </Form>
                        </Loader>
                    )}
                </Formik>
            </Modal>
            </ThemeProviderV2>
        );
    }, [ userId, isSubmitting, name, status, onAuthError]);

    return {
        isSubmitting,
        handler: (name, status) => {
            setName(name);
            setStatus(status);
            show();
        },
    };
};

export default useReviewRejectHandler;
